/**
 * Weather data fetchers — Open-Meteo + NWS Alerts.
 *
 * Domain responsibilities:
 *   - Southern Maine coordinates and NWS grid resolution
 *   - Barometric pressure trend interpretation (rising/falling/steady)
 *   - NWS alert filtering to fishing-relevant event types
 *   - Result formatting for LLM context injection
 *
 * No API key required for either service.
 * NWS requires User-Agent header — all requests include it.
 */

/** Southern Maine centroid for weather queries */
const WEATHER_LAT = 43.661;
const WEATHER_LON = -70.255;

/* ── Open-Meteo ─────────────────────────────────────────────────────── */

export interface AtmosphericConditions {
  airTempC: number;
  airTempF: number;
  pressureHpa: number;
  pressureTrend: "rising" | "steady" | "falling";
  windSpeedKph: number;
  windDirection: number;
  uvIndex: number;
  precipMm: number;
  /** Formatted string for LLM injection */
  summary: string;
}

/**
 * Fetch current atmospheric conditions from Open-Meteo.
 * Also fetches the last 3 hours of pressure to compute trend.
 * Free, no API key, 10K calls/day.
 */
export async function getAtmosphericConditions(
  lat = WEATHER_LAT,
  lon = WEATHER_LON
): Promise<AtmosphericConditions | null> {
  try {
    const params = new URLSearchParams({
      latitude: lat.toString(),
      longitude: lon.toString(),
      current:
        "temperature_2m,precipitation,wind_speed_10m,wind_direction_10m,sea_level_pressure,uv_index",
      hourly: "sea_level_pressure",
      timezone: "America/New_York",
      forecast_days: "1",
    });

    const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;

    const data = await res.json();
    const c = data.current;
    if (!c) return null;

    // Compute pressure trend from last 3h of hourly data
    const pressures: number[] = data.hourly?.sea_level_pressure ?? [];
    const trend = computePressureTrend(pressures);

    const airTempC = c.temperature_2m ?? 0;
    const pressureHpa = c.sea_level_pressure ?? 1013;
    const windSpeedKph = c.wind_speed_10m ?? 0;
    const windDirection = c.wind_direction_10m ?? 0;
    const uvIndex = c.uv_index ?? 0;
    const precipMm = c.precipitation ?? 0;

    const trendStr = trend === "rising" ? "📈 rising" : trend === "falling" ? "📉 falling" : "→ steady";
    const windDirStr = degreesToCompass(windDirection);

    const summary = [
      `Air Temp: ${airTempC.toFixed(1)}°C (${cToF(airTempC).toFixed(0)}°F)`,
      `Barometric Pressure: ${pressureHpa.toFixed(1)} hPa — ${trendStr}`,
      trend === "falling"
        ? "⚡ Falling pressure = fish are actively feeding — prime conditions"
        : trend === "rising"
          ? "Rising pressure — fish may become less active as weather improves"
          : "Stable pressure — normal activity expected",
      `Wind: ${windSpeedKph.toFixed(0)} km/h ${windDirStr}`,
      uvIndex >= 7
        ? `UV Index: ${uvIndex} (HIGH — fish shaded pockets, wear protection)`
        : `UV Index: ${uvIndex}`,
      precipMm > 0 ? `Precipitation: ${precipMm.toFixed(1)}mm — check river levels before wading` : "",
    ]
      .filter(Boolean)
      .join("\n");

    return {
      airTempC,
      airTempF: cToF(airTempC),
      pressureHpa,
      pressureTrend: trend,
      windSpeedKph,
      windDirection,
      uvIndex,
      precipMm,
      summary,
    };
  } catch {
    return null;
  }
}

function cToF(c: number): number {
  return (c * 9) / 5 + 32;
}

function degreesToCompass(deg: number): string {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(deg / 45) % 8];
}

function computePressureTrend(pressures: number[]): "rising" | "steady" | "falling" {
  if (pressures.length < 2) return "steady";
  const recent = pressures.slice(-3);
  const oldest = recent[0];
  const newest = recent[recent.length - 1];
  const delta = newest - oldest;
  if (delta > 1.0) return "rising";
  if (delta < -1.0) return "falling";
  return "steady";
}

/* ── NWS Alerts ─────────────────────────────────────────────────────── */

/** NWS alert event types relevant to fly fishing safety */
const FISHING_ALERT_EVENTS = new Set([
  "Flash Flood Warning",
  "Flash Flood Watch",
  "Flood Warning",
  "Flood Watch",
  "Flood Advisory",
  "Severe Thunderstorm Warning",
  "Severe Thunderstorm Watch",
  "Wind Advisory",
  "High Wind Warning",
  "High Wind Watch",
  "Small Craft Advisory",
  "Gale Warning",
  "Special Marine Warning",
]);

export interface NWSAlert {
  event: string;
  headline: string;
  description: string;
  severity: string;
  urgency: string;
  onset: string;
  expires: string;
  areas: string;
  /** True if this involves flooding — show on all river pages */
  isFloodRelated: boolean;
}

/**
 * Fetch active NWS weather alerts for Maine.
 * Filters to fishing-relevant events only.
 * Free, no API key. Poll at most every 5 minutes.
 */
export async function getNWSAlerts(state = "ME"): Promise<NWSAlert[]> {
  try {
    const res = await fetch(
      `https://api.weather.gov/alerts/active?area=${state}`,
      {
        headers: {
          Accept: "application/json",
          "User-Agent": "FlyFishME/1.0 (field guide PWA — open source)",
        },
      }
    );
    if (!res.ok) return [];

    const data = await res.json();
    const features = data?.features ?? [];

    return features
      .filter((f: { properties?: { event?: string } }) =>
        FISHING_ALERT_EVENTS.has(f.properties?.event ?? "")
      )
      .map((f: { properties?: Record<string, string> }) => {
        const p = f.properties ?? {};
        const event = p.event ?? "Unknown";
        return {
          event,
          headline: p.headline ?? event,
          description: p.description ?? "",
          severity: p.severity ?? "Unknown",
          urgency: p.urgency ?? "Unknown",
          onset: p.onset ?? "",
          expires: p.expires ?? "",
          areas: p.areaDesc ?? "",
          isFloodRelated:
            event.toLowerCase().includes("flood") ||
            event.toLowerCase().includes("flash"),
        };
      });
  } catch {
    return [];
  }
}
